import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Crown, Phone, RotateCcw, Sparkles,
  Eye, Coins,
  Target, Gem,
  ArrowRight, Zap, Headphones,
  Waves, Hand,
} from "lucide-react";
import YourEdgeToday from "./YourEdgeToday";
import { GuideEntry } from "../../components/coach/GuideEntry";
import { isEdgeOnboarded } from "./EdgeOnboarding";
import { markEdgeUsedToday } from "./edgeUsageStore";
import CollectiveCommunityLink from "../../components/CollectiveCommunityLink";
import { premiumCard, premiumPageBg, premiumHeading, premiumEyebrow, ACCENTS } from "../../utils/premiumStyle";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ────────────────────────────────────────────────────────────────────
 * Networker landing — restructured per the user's spec:
 *
 *   1. "Your Edge Today" (single dynamic card)
 *   2. Daily Edge       — Identity Activation, Pre-Call, Rejection,
 *                          Confidence Reset (the four anchor actions)
 *   3. Deep Work        — Instant Manifestation, Future Team,
 *                          Wealth Normalisation (Guided Performance)
 *   4. Your Tools       — Living Vision, Bank of Reclaim
 *   5. Explore          — Challenges, Affirmations
 *
 * Design rule: the eye should land on YourEdgeToday first. Daily Edge
 * is the second-strongest visual layer. Everything else recedes.
 *
 * First-visit: redirect to /networker/onboarding once.
 * ──────────────────────────────────────────────────────────────── */

const DAILY_EDGE = [
  {
    id: "identity_activation",
    title: "Identity Activation",
    sub: "Set who you are today",
    icon: Crown,
    to: "/networker/audio-library?play=edge_daily_money_identity",
    accent: "rgba(220,180,110,0.55)",
  },
  {
    id: "pre_call",
    title: "Pre-Call Reset",
    sub: "Before your next call",
    icon: Phone,
    to: "/networker/resets/pre_call",
    accent: "rgba(217,165,90,0.55)",
  },
  {
    id: "rejection",
    title: "Rejection Reset",
    sub: "Return to forward motion",
    icon: RotateCcw,
    to: "/networker/resets/rejection",
    accent: "rgba(220,130,130,0.55)",
  },
  {
    id: "confidence",
    title: "Confidence Reset",
    sub: "Lead from your edge",
    icon: Sparkles,
    to: "/networker/resets/confidence",
    accent: "rgba(220,180,110,0.55)",
  },
];

const DEEP_WORK = [
  {
    id: "edge_daily_money_identity",
    title: "Daily Money Identity",
    sub: "Anchor your identity. Loops your affirmations.",
    icon: Crown,
    to: "/networker/audio-library?play=edge_daily_money_identity",
  },
  {
    id: "edge_instant_manifestation",
    title: "Instant Manifestation",
    sub: "Step into the version already leading",
    icon: Sparkles,
    to: "/networker/audio-library?play=edge_instant_manifestation",
  },
  {
    id: "edge_future_team",
    title: "Future Team",
    sub: "See your organisation alive",
    icon: Headphones,
    to: "/networker/audio-library?play=edge_future_team",
  },
  {
    id: "edge_wealth_normalisation",
    title: "Wealth Normalisation",
    sub: "Soften into bigger numbers",
    icon: Coins,
    to: "/networker/audio-library?play=edge_wealth_normalisation",
  },
];

const YOUR_TOOLS = [
  { id: "vision", title: "Living Vision",              sub: "See where you're going",            icon: Eye,   to: "/networker/performance-vision/setup" },
  { id: "bank",   title: "Bank of Reclaim",            sub: "Normalise the numbers",             icon: Coins, to: "/networker/intro/bank-of-reclaim" },
  { id: "prime",  title: "Pre-Performance Priming",    sub: "Warm-up for your key moments",      icon: Zap,   to: "/networker/prime", accent: "rgba(217,180,120,0.7)" },
  { id: "suite",  title: "Private Suite",              sub: "Personal deep installation",        icon: Crown, to: "/networker/intro/private-suite" },
];

const EXPLORE = [
  { id: "challenges",   title: "Challenges",   sub: "Multi-day guided programs",  icon: Target, to: "/networker/challenges" },
  { id: "affirmations", title: "Affirmations", sub: "Rewire your mindset",        icon: Gem,    to: "/networker/affirmations" },
];

// "My Practices" — daily-use ritual layer (Resets · State Tuning · Tapping)
const MY_PRACTICES = [
  { id: "resets",       title: "Resets",       sub: "Come back in seconds",       icon: RotateCcw, to: "/practices/resets",       accent: "rgba(217,180,120,0.55)" },
  { id: "state-tuning", title: "State Tuning", sub: "Shift your state with tone", icon: Waves,     to: "/practices/state-tuning", accent: "rgba(190,205,235,0.55)" },
  { id: "tapping",      title: "Tapping",      sub: "Release what's in the way",  icon: Hand,      to: "/practices/tapping",      accent: "rgba(215,195,225,0.55)" },
];

function SectionHeading({ label, blurb }) {
  return (
    <div className="px-6 mt-9 mb-3">
      <p className="text-[10px] uppercase tracking-[0.4em] text-amber-500/75 mb-1.5">
        {label}
      </p>
      {blurb && <p className="text-white/45 text-xs">{blurb}</p>}
    </div>
  );
}

function PrimaryRow({ item }) {
  const navigate = useNavigate();
  const Icon = item.icon;
  const handleClick = () => {
    markEdgeUsedToday();
    navigate(item.to);
  };
  return (
    <button
      onClick={handleClick}
      data-testid={`edge-action-${item.id}`}
      className="group w-full text-left rounded-2xl px-4 py-3.5 flex items-center gap-4 transition-all duration-300 hover:-translate-y-0.5"
      style={premiumCard({ accent: ACCENTS.networker, goldKiss: true })}
    >
      <div
        className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{
          background:
            "linear-gradient(135deg, rgba(241,210,156,0.20), rgba(241,210,156,0.04))",
          boxShadow: `0 6px 18px -10px ${item.accent || "rgba(241,210,156,0.45)"}`,
        }}
      >
        <Icon className="w-5 h-5 text-amber-200" strokeWidth={1.6} />
      </div>
      <div className="flex-1 min-w-0">
        <h3
          className="text-white text-[15px] font-semibold leading-tight"
          style={{ letterSpacing: "-0.005em" }}
        >
          {item.title}
        </h3>
        <p className="text-white/45 text-xs mt-0.5">{item.sub}</p>
      </div>
      <ArrowRight className="w-4 h-4 text-white/30 group-hover:text-amber-300 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
    </button>
  );
}

function SecondaryRow({ item }) {
  const navigate = useNavigate();
  const Icon = item.icon;
  return (
    <button
      onClick={() => navigate(item.to)}
      data-testid={`edge-secondary-${item.id}`}
      className="group w-full text-left rounded-2xl px-4 py-3 flex items-center gap-3 transition-colors hover:bg-white/[0.04]"
      style={premiumCard({ accent: ACCENTS.networker, density: 'low' })}
    >
      <div className="w-8 h-8 rounded-md bg-white/[0.04] flex items-center justify-center flex-shrink-0 border border-amber-300/10">
        <Icon className="w-4 h-4 text-white/55" strokeWidth={1.6} />
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-white/90 text-sm font-medium leading-tight">
          {item.title}
        </h4>
        <p className="text-white/35 text-[11px] mt-0.5">{item.sub}</p>
      </div>
      <ArrowRight className="w-3.5 h-3.5 text-white/25 group-hover:text-white/55 transition-colors flex-shrink-0" />
    </button>
  );
}

const NetworkerLandingPage = () => {
  const navigate = useNavigate();
  const [_stats, setStats] = useState({ active: 0, completed: 0, total: 0 });

  // First-visit gate to onboarding.
  useEffect(() => {
    if (!isEdgeOnboarded()) {
      navigate("/networker/onboarding", { replace: true });
    }
  }, [navigate]);

  // Pull challenge stats silently in the background — used by future
  // smart-suggestions; not rendered as a banner anymore (the spec said
  // remove equal-weight options).
  useEffect(() => {
    const t = localStorage.getItem("reclaim_token");
    if (!t) return;
    fetch(`${API_URL}/api/networker/challenges`, {
      headers: { Authorization: `Bearer ${t}` },
    })
      .then((r) => r.json())
      .then((data) => {
        let active = 0, completed = 0;
        (data.challenges || []).forEach((c) => {
          if (c.user_progress?.is_active) active++;
          if (c.user_progress?.completed_days?.length === c.duration_days) completed++;
        });
        setStats({ active, completed, total: data.challenges?.length || 0 });
      })
      .catch(() => {});
  }, []);

  return (
    <div
      className="min-h-screen"
      style={premiumPageBg()}
      data-testid="networker-landing"
    >
      {/* Header — "THE EDGE" */}
      <div className="px-6 pt-12 pb-6" data-testid="edge-header">
        <p style={premiumEyebrow} className="mb-3">
          THE EDGE
        </p>
        <h1
          className="text-4xl text-white leading-tight"
          style={{ ...premiumHeading, fontSize: 'clamp(2.25rem, 5.5vw, 3rem)' }}
        >
          High Performance Layer
        </h1>
        <p className="text-white/55 text-sm mt-2 max-w-md italic"
           style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: "15px" }}>
          Use this daily. Keep it simple.
        </p>
      </div>

      {/* 1. YOUR EDGE TODAY */}
      <div className="px-6 mb-2">
        <YourEdgeToday />
        <div className="mt-6"><GuideEntry coach="networker" to="/networker/guide" label="Networker" /></div>
        <button type="button" onClick={() => window.location.assign("/networker/immersion")} data-testid="networker-immersion-entry"
          className="mt-4 w-full rounded-2xl px-5 py-4 text-left transition-transform duration-200 hover:-translate-y-0.5"
          style={{ background: "linear-gradient(135deg, rgba(64,42,18,0.85), rgba(18,12,6,0.95))", border: "1px solid rgba(215,187,122,0.35)" }}>
          <span className="block text-[10px] uppercase tracking-[0.3em]" style={{ color: "#D7BB7A" }}>Immersion · 6 minutes</span>
          <span className="block mt-1 text-xl" style={{ color: "#F1ECDD", fontFamily: '"Cormorant Garamond", Georgia, serif' }}>Walk in already connected</span>
        </button>
      </div>

      {/* 2. DAILY EDGE */}
      <SectionHeading
        label="Daily Edge"
        blurb={(
          <>
            The four anchors.<br />
            Stay consistent. Feel the difference.
          </>
        )}
      />
      <div className="px-6 space-y-2.5">
        {DAILY_EDGE.map((it) => <PrimaryRow key={it.id} item={it} />)}
      </div>

      {/* 3. DEEP WORK */}
      <SectionHeading label="Deep Work" blurb="When you have a moment to install the identity." />
      <div className="px-6 space-y-2">
        {DEEP_WORK.map((it) => <PrimaryRow key={it.id} item={it} />)}
      </div>

      {/* 4. YOUR TOOLS */}
      <SectionHeading label="Your Tools" />
      <div className="px-6 space-y-2">
        {YOUR_TOOLS.map((it) => <PrimaryRow key={it.id} item={it} />)}
      </div>

      {/* 4.5 MY PRACTICES — daily-use ritual layer */}
      <SectionHeading label="My Practices" blurb="The daily-use ritual layer. Use any. Use often." />
      <div className="px-6 space-y-2" data-testid="edge-my-practices">
        {MY_PRACTICES.map((it) => <PrimaryRow key={it.id} item={it} />)}
      </div>

      {/* 5. EXPLORE */}
      <SectionHeading label="Explore" />
      <div className="px-6 space-y-2 pb-32">
        {EXPLORE.map((it) => <SecondaryRow key={it.id} item={it} />)}
        <div className="pt-2">
          <CollectiveCommunityLink
            community="networker_edge"
            label="The Networker collective"
            sublabel="Drop into the shared field of operators today"
            data-testid="networker-collective-link"
          />
        </div>
      </div>

      {/* Footer micro-anchor */}
      <div className="fixed bottom-0 left-0 right-0 p-6 pointer-events-none"
           style={{ background: "linear-gradient(to top, #050402 0%, rgba(5,4,2,0.95) 50%, transparent 100%)" }}>
        <div className="flex items-center justify-center gap-2 text-white/35 text-[10px] uppercase tracking-[0.4em]">
          <Zap className="w-3 h-3 text-amber-500/70" strokeWidth={1.8} />
          <span>State · Identity · Momentum</span>
        </div>
      </div>
    </div>
  );
};

export default NetworkerLandingPage;
